import * as React from 'react';
import { useMemo, memo, useCallback } from 'react';
import { atom, useAtom, useAtomValue, useSetAtom } from 'jotai';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';

import { CUSTOM_BASE_URL, imageVariant, useAmbassadorData, useAnalytics } from '@restworld/utils-common';
import { useAuth } from '@restworld/feature-authentication';

import { ExplorerMapPopupCard, ExplorerMapPopupCardProps } from './explorer-map-popup-card';
import { getSalary } from '../../../hooks';
import { explorerRestaurantSelectedMarkerAtom } from '../job-explorer-restaurant-explorer.atoms';
import {
  getPopupDetailsAtom,
  mapPopupLoadingAtom,
  setPopupDetailsAtom,
  setPopupLocationCoordinatesAtom,
} from '../job-explorer-map.atoms';
import { activeJpIdAtom, jobListParamsAtom } from '../job-explorer-list.atoms';
import { checkIfJobPositionIsOutsideItaly } from '../../../utils';

export const isRestaurantMarkerAtom = atom<boolean>(false);
const useRestaurantDetails = () => {
  const popupDetails = useAtomValue(getPopupDetailsAtom);
  const { t } = useTranslation('common');

  const salary = useMemo(() => getSalary({ jobPositionApiData: popupDetails, t }), [popupDetails, t]);

  const otherInfos = useMemo(() => {
    const shiftsTitle = popupDetails?.shift_category_var?.label || '';
    const infos: string[] = [];
    if (shiftsTitle) infos.push(shiftsTitle);
    if (salary) infos.push(salary);
    return infos;
  }, [salary, popupDetails?.shift_category_var?.label]);

  // Popup card background uses the first image at ~600px wide → `_md` is plenty.
  const images = useMemo(
    () => popupDetails?.restaurant?.pictures?.map((v) => imageVariant(v.url, 'md') ?? v.url) || [],
    [popupDetails?.restaurant?.pictures]
  );

  // Restaurant logo on the popup card is small avatar-sized → `_sm`.
  const restaurantLogo = useMemo(() => {
    return imageVariant(popupDetails?.restaurant?.restaurant_logo, 'sm') ?? null;
  }, [popupDetails?.restaurant?.restaurant_logo]);

  const bgImage = useMemo(() => images[0] || null, [images]);

  return { salary, otherInfos, images, restaurantLogo, bgImage };
};

const useRedirectUrl = () => {
  const explorerRestaurantSelectedMarker = useAtomValue(explorerRestaurantSelectedMarkerAtom);
  const popupDetails = useAtomValue(getPopupDetailsAtom);
  const isRestaurantMarker = useAtomValue(isRestaurantMarkerAtom);

  const { locale } = useRouter();
  const ambData = useAmbassadorData();

  return useMemo(() => {
    if (isRestaurantMarker && explorerRestaurantSelectedMarker?.slug) {
      return `${CUSTOM_BASE_URL}/${locale}/locale/${explorerRestaurantSelectedMarker.slug}`;
    }
    if (popupDetails?.semantic_unique_name) {
      const baseUrl = `${CUSTOM_BASE_URL}/${locale}/posizione/${popupDetails.semantic_unique_name}`;
      return ambData?.ambassador_code
        ? `${baseUrl}?utm_source=referral&utm_medium=ambassador&utm_campaign=worker_acquisition&ref_code=${ambData.ambassador_code}`
        : baseUrl;
    }
    return '';
  }, [ambData?.ambassador_code, explorerRestaurantSelectedMarker?.slug, isRestaurantMarker, locale, popupDetails]);
};

const useExplorerMapCardProps = () => {
  const { otherInfos, restaurantLogo, bgImage } = useRestaurantDetails();
  const popupDetails = useAtomValue(getPopupDetailsAtom);
  const setPopupDetails = useSetAtom(setPopupDetailsAtom);
  const setPopupLocationCoordinates = useSetAtom(setPopupLocationCoordinatesAtom);
  const popupLoading = useAtomValue(mapPopupLoadingAtom);
  const isRestaurantMarker = useAtomValue(isRestaurantMarkerAtom);
  const setActiveJpId = useSetAtom(activeJpIdAtom);

  const [explorerRestaurantSelectedMarker, setExplorerRestaurantSelectedMarker] = useAtom(
    explorerRestaurantSelectedMarkerAtom
  );
  return useMemo<ExplorerMapPopupCardProps>(
    () => ({
      ...(isRestaurantMarker
        ? { companyTitle: explorerRestaurantSelectedMarker?.restaurant_name }
        : { companyTitle: popupDetails?.restaurant?.restaurant_name ?? '-' }),
      ...(!isRestaurantMarker ? { jpTitle: popupDetails?.job_title ?? '-' } : {}),
      companyLocation: isRestaurantMarker
        ? explorerRestaurantSelectedMarker?.address ?? '-'
        : !popupDetails?.show_restaurant_details
        ? popupDetails?.restaurant?.locality ?? '-'
        : popupDetails?.restaurant?.address ?? '-',
      ...(!isRestaurantMarker ? { otherInfos } : {}),
      onCardClose(e) {
        e.preventDefault();
        setPopupLocationCoordinates(null);
        setActiveJpId(null);
        if (isRestaurantMarker) setExplorerRestaurantSelectedMarker(null);
        else setPopupDetails({});
      },
      ...(!isRestaurantMarker
        ? { bgImage }
        : {
            bgImage: imageVariant(explorerRestaurantSelectedMarker?.pictures?.[0]?.url, 'md') ?? undefined,
          }),
      ...(isRestaurantMarker
        ? { restaurantLogo: imageVariant(explorerRestaurantSelectedMarker?.restaurant_logo, 'sm') ?? undefined }
        : { restaurantLogo }),
      loading: popupLoading,
      ...(isRestaurantMarker ? {} : { showRestaurantDetails: popupDetails?.show_restaurant_details }),
      isRestaurantsExplorer: isRestaurantMarker,
      ...(isRestaurantMarker && explorerRestaurantSelectedMarker?.gmaps_info?.info
        ? {
            googleMapsInfo: {
              rating: explorerRestaurantSelectedMarker?.gmaps_info.info.rating,
              reviews: explorerRestaurantSelectedMarker?.gmaps_info.info.user_ratings_total,
            },
          }
        : {}),
    }),
    [
      isRestaurantMarker,
      explorerRestaurantSelectedMarker?.restaurant_name,
      explorerRestaurantSelectedMarker?.address,
      explorerRestaurantSelectedMarker?.pictures,
      explorerRestaurantSelectedMarker?.restaurant_logo,
      explorerRestaurantSelectedMarker?.gmaps_info?.info,
      popupDetails?.restaurant?.restaurant_name,
      popupDetails?.restaurant?.locality,
      popupDetails?.restaurant?.address,
      popupDetails?.job_title,
      popupDetails?.show_restaurant_details,
      otherInfos,
      bgImage,
      restaurantLogo,
      popupLoading,
      setPopupLocationCoordinates,
      setActiveJpId,
      setExplorerRestaurantSelectedMarker,
      setPopupDetails,
    ]
  );
};

const PopupInfoEl: React.FunctionComponent = () => {
  const popupDetails = useAtomValue(getPopupDetailsAtom);
  const sortFilterParams = useAtomValue(jobListParamsAtom);

  const { trackEvent } = useAnalytics();
  const auth = useAuth();

  const explorerMapCardProps = useExplorerMapCardProps();

  const recordViewJobPositionEvent = useCallback(() => {
    const properties = {
      email: auth?.user?.email,
      role: auth?.user?.type,
      origin: 'job_explorer_map',
      id: popupDetails?.id,
      job_title: popupDetails?.job_title,
      occupation: popupDetails?.occupation?.label_it?.label,
      salary_min: popupDetails?.min_salary,
      salary_max: popupDetails?.max_salary,
      job_type: popupDetails?.job_type_var?.label,
      published_at: popupDetails?.published_at,
      restaurant_name: popupDetails?.restaurant?.restaurant_name,
      city: popupDetails?.restaurant?.locality,
      searchParams: sortFilterParams,
    };
    trackEvent('view_job', { ...properties });
  }, [auth?.user, popupDetails, sortFilterParams, trackEvent]);

  const redirectUrl = useRedirectUrl();

  const redirectCallback = useCallback(() => {
    if (redirectUrl) window.open(redirectUrl, '_blank', 'noopener,noreferrer');
  }, [redirectUrl]);

  const onJobPositionPopupElClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
      e.preventDefault();
      e.stopPropagation();
      if (popupDetails?.semantic_unique_name) recordViewJobPositionEvent();
      redirectCallback();
    },
    [recordViewJobPositionEvent, redirectCallback, popupDetails]
  );

  const isJobPositionOutsideItaly = checkIfJobPositionIsOutsideItaly({
    address: popupDetails?.restaurant?.address,
    country: popupDetails?.restaurant?.country,
  });

  return (
    <div className="cursor-pointer p-6 lg:p-0 z-10 md:z-[20]" onMouseDown={onJobPositionPopupElClick}>
      <ExplorerMapPopupCard {...explorerMapCardProps} isJobPositionOutsideItaly={isJobPositionOutsideItaly} />
    </div>
  );
};

export default memo(PopupInfoEl);
